import { createAsyncThunk } from "@reduxjs/toolkit";

import includes from "lodash/includes";

import { changeSignupFormResponses, getSignupFormResponses } from "@/models";
import { AppState } from "@/redux/store/store";
import { fetchAuthToken } from "@/helpers/fetch-auth-token";

export const fetchSignupFormResponses = createAsyncThunk(
  "signupFormResponses/fetchSignupFormResponses",
  async (_, { getState, rejectWithValue }) => {
    try {
      const { adapter, user, signupFormQuestions } = getState() as AppState;
      const { baseUrl } = adapter.adapter;
      const { profile } = user.registeredUser;
      const { token } = fetchAuthToken(getState());
      const questions = signupFormQuestions.signupFormQuestions;

      const questionIdAndTypesOfSignUpLoginAndEmail = questions.reduce(
        (acc, { id, type }) => {
          if (
            includes(
              ["SignupForm::LoginQuestion", "SignupForm::AccountEmailQuestion"],
              type
            )
          ) {
            acc.push({
              id,
              type: type === "SignupForm::LoginQuestion" ? "login" : "email"
            });
          }
          return acc;
        },
        []
      );

      const response = await getSignupFormResponses(token, baseUrl);

      const preFillMissingResponses =
        questionIdAndTypesOfSignUpLoginAndEmail.reduce((acc, { id, type }) => {
          if (!response?.flattenedAnswers[id]?.value) {
            acc[id] = { value: profile[type] };
          }
          return acc;
        }, {});

      return { ...response?.flattenedAnswers, ...preFillMissingResponses };
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

// The GET returns every answer the user has ever stored, including ones whose
// questions were dropped when the signup form was edited. Sending those back
// fails validation against the current form, so keep only what still belongs to
// it. Matches on the whole form rather than the currently visible questions, so
// answers behind an untriggered condition survive the round trip.
export const currentFormAnswers = (
  answers: { [key: string]: unknown },
  formQuestions: Array<{ id: string | number }>
) => {
  if (!formQuestions?.length) return answers;

  const questionIds = new Set(formQuestions.map(({ id }) => `${id}`));

  return Object.fromEntries(
    Object.entries(answers).filter(([questionId]) =>
      questionIds.has(questionId)
    )
  );
};

export const updateSignupFormResponses = createAsyncThunk(
  "signupFormResponses/updateSignupFormResponses",
  async (_, { getState, rejectWithValue }) => {
    try {
      const { adapter, questions, signupFormQuestions } =
        getState() as AppState;
      const { baseUrl } = adapter.adapter;
      const { answers } = questions;
      const { token } = fetchAuthToken(getState());

      const response = await changeSignupFormResponses(token, baseUrl, {
        answers: currentFormAnswers(
          answers,
          signupFormQuestions.signupFormQuestions
        )
      });

      return response;
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);
